<template>
  <div>
    <h5 v-if="!noTitle">{{ trans('payment_options') }}</h5>
    <b-row>
      <b-col cols="6">
        <b-form-group style="margin: 0">
          <label><strong>{{ trans('payment_options_about') }}</strong></label>
          <b-form-radio-group
            stacked
            v-model="pay"
            :disabled="disabled"
          >
            <b-form-radio
              v-for="option in options"
              :key="option.value"
              :value="option.value"
              :disabled="!!option.disabled"
              :class="`mb-2 ${option.value == 'cc_or_interac' ? 'mb-0' : ''}`"
            >
              <span>{{ option.text }}</span>
              <small v-if="pay == option.value" class="d-block" v-html="option.description"></small>
            </b-form-radio>
          </b-form-radio-group>
        </b-form-group>
      </b-col>
      <b-col cols="6">
        <div v-if="!noDeposit && pay != 'offline' && pay != 'gift_card'">
          <p>
            <strong>{{ trans('deposit') }}</strong>
            <small class="d-block">{{ trans('deposit_about') }}</small>
          </p>
          <b-form-group :label="trans(showPercentage ? 'min_deposit' : 'amount') + ' ($)'">
            <b-form-input
              type="number"
              min="0"
              size="sm"
              class="w-75"
              v-model="dep"
              :state="depositState"
              :disabled="disabled || depositDisabled"
            />
            <b-form-text>{{ trans('leave_min_blank_for_no_deposit') }}</b-form-text>
          </b-form-group>
          <b-form-group v-if="showPercentage && depositState" :label="trans('deposit_percentage') + ' (%)'">
            <b-form-input
              type="number"
              min="0"
              max="100"
              size="sm"
              class="w-75"
              v-model="per"
              :state="percentageState"
              :disabled="disabled || depositDisabled"
            />
            <b-form-text>{{ trans('leave_percentage_blank_for_amount') }}</b-form-text>
          </b-form-group>
        </div>

        <b-form-group v-if="type == 'package'">
          <label>
            {{ trans('requires_coupon') }}
          </label>
          <b-form-checkbox v-model="coup" switch :disabled="disabled">
            {{ coup ? trans('yes') : trans('no')}}
          </b-form-checkbox>
          <b-form-text v-if="coup">
            {{ trans(type + '_requires_coupon_about') }}
          </b-form-text>
        </b-form-group>

        <b-form-group v-if="type == 'package' && organization.has_memberships">
          <label>
            {{ trans('requires_membership') }}
          </label>
          <b-form-checkbox v-model="memb" switch :disabled="disabled">
            {{ memb ? trans('yes') : trans('no')}}
          </b-form-checkbox>
          <b-form-text v-if="memb">
            {{ trans(type + '_requires_membership_about') }}
          </b-form-text>
        </b-form-group>
      </b-col>
    </b-row>
  </div>
</template>

<script>

  function deserializePayment(value) {
    let pay = value
    if (value == 'deposit') {
      pay = 'online'
    }
    else if (value == 'deposit_interac') {
      pay = 'interac'
    }
    else if (value == 'deposit_cc_or_interac') {
      pay = 'cc_or_interac'
    }
    return pay
  }

  export default {
    props: {
      organization: Object,
      value: String, // online, deposit, offline, interac or cc_or_interac
      label: String,
      type: String, // form, cart, package, product, membership, null (assume form when null)
      deposit: String,
      deposit_percentage: String|Number,
      alwaysOnline: {
        type: Boolean,
        default: false,
      },
      noDeposit: {
        type: Boolean,
        default: false,
      },
      requires_coupon: Boolean, // only for type package
      requires_membership: Boolean, // only for type package
      disabled: {
        type: Boolean,
        default: false,
      },
      depositDisabled: {
        type: Boolean,
        default: false,
      },
      noTitle: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        trans: trans,
        pay: deserializePayment(this.value),
        dep: this.deposit,
        per: this.deposit_percentage > 0 ? this.deposit_percentage*100 : null,
        coup: this.requires_coupon,
        memb: this.requires_membership,
      }
    },
    computed: {
      options() {
        let options = []
        if (this.type == 'cart') {
          options.push({
            value: null,
            text: trans('not_specified'),
            description: trans('payment_option_admin_cart_blank'),
          })
        }
        if (!this.alwaysOnline) {
          options.push({
            value: 'offline',
            text: trans('offline'),
            description: trans('payment_option_offline'),
          })
        }
        options.push({
          value: 'online',
          text: trans('credit_card'),
          description: trans('payment_option_online'),
          disabled: !this.organization.has_stripe,
        })
        if (this.type == 'form') {
          options.push({
            value: 'gift_card',
            text: trans('gift_card'),
            description: trans('payment_option_gift_card'),
          })
        }
        if (this.organization.interac_email) {
          if (!this.alwaysOnline) {
            options.push({
              value: 'interac',
              text: trans('interac'),
              description: trans('payment_option_interac').replace(':email', this.organization.interac_email),
            })
          }
          options.push({
            value: 'cc_or_interac',
            text: trans('cc_or_interac'),
            description: trans('payment_option_cc_or_interac'),
            disabled: !this.organization.has_stripe,
          })
        }
        return options
      },
      depositState() {
        if (this.dep === '0' || this.dep === 0) return false
        if (!this.dep ||
          typeof this.dep == 'string' && this.dep.trim() === '')
          return null
        return this.dep > 0
      },
      showPercentage() {
        return this.deposit_percentage !== undefined
      },
      percentageState() {
        if (!this.per ||
          typeof this.per == 'string' && this.per.trim() === '')
          return null
        return this.per > 0 && this.per < 100
      },
      serializedPayment() {
        if (this.pay == 'online' && this.depositState === true)
          return 'deposit'
        if (this.pay != 'offline' && this.pay != 'gift_card' && this.depositState === true)
          return 'deposit_' + this.pay
        return this.pay
      },
    },
    watch: {
      pay() {
        this.$emit('input', this.serializedPayment)
      },
      dep() {
        this.$emit('deposit', this.depositState == true ? this.dep : null)
        if (this.value != this.serializedPayment)
          this.$emit('input', this.serializedPayment)
      },
      per() {
        if (!this.showPercentage) return
        this.$emit('deposit_percentage', this.percentageState == true ? this.per/100 : null)
        if (this.value != this.serializedPayment)
          this.$emit('input', this.serializedPayment)
      },
      value() {
        this.pay = deserializePayment(this.value)
      },
      deposit() {
        this.dep = this.deposit
      },
      deposit_percentage() {
        this.per = this.deposit_percentage > 0 ? this.deposit_percentage*100 : null
      },
      coup() {
        this.$emit('requires_coupon', this.coup)
      },
      memb() {
        this.$emit('requires_membership', this.memb)
      },
      requires_coupon() {
        this.coup = this.requires_coupon
      },
      requires_membership() {
        this.memb = this.requires_membership
      },
    },
    mounted() {
      window.paymentOptions = this
    },
  }
</script>